<template>
  <div>
    <header class="global-navigation flex items-center">
      <NavigationLogo v-if="isAuthenticated" to="/account/seekers/dashboard" />
      <NavigationLogo v-else href="/" />
    </header>
    <section class="flex align-middle justify-center sm:py-40">
      <Card class="w-full max-w-448 p-24 box-shadow-none sm:box-shadow-sm">
        <div v-if="loading" role="status" aria-live="polite">
          <span class="sr-only">Verifying your employer benefit…</span>
          <Spinner aria-hidden="true" :width="96" :height="96" class="mx-auto my-96" />
        </div>
        <VerifyCorporateUpgradeSuccess v-else-if="pageState === 'success'" />
        <VerifyCorporateUpgradeLoggedOut
          v-else-if="pageState === 'logged_out'"
          :masked-member-email="maskedMemberEmail"
          :login-url="loginUrl"
        />
        <VerifyCorporateUpgradeWrongAccount v-else-if="pageState === 'wrong_account'" :masked-member-email="maskedMemberEmail" />
        <VerifyCorporateUpgradeExistingPremium v-else-if="pageState === 'existing_premium'" />
        <VerifyCorporateUpgradeInvalidLink v-else />
      </Card>
    </section>
  </div>
</template>

<script lang="ts">
import Card from '@/components/card/Card.vue';
import NavigationLogo from '@/components/nav/components/NavigationLogo.vue';
import Spinner from '@/components/spinner/Spinner.vue';
import VerifyCorporateUpgradeSuccess from './components/VerifyCorporateUpgradeSuccess.vue';
import VerifyCorporateUpgradeLoggedOut from './components/VerifyCorporateUpgradeLoggedOut.vue';
import VerifyCorporateUpgradeWrongAccount from './components/VerifyCorporateUpgradeWrongAccount.vue';
import VerifyCorporateUpgradeExistingPremium from './components/VerifyCorporateUpgradeExistingPremium.vue';
import VerifyCorporateUpgradeInvalidLink from './components/VerifyCorporateUpgradeInvalidLink.vue';
import { CONSUME_CORPORATE_VERIFICATION_TOKEN } from '@/graphql/bh_benefits/mutations';
import { CORPORATE_VERIFICATION_TOKEN_QUERY } from '@/graphql/bh_benefits/queries';
import { CORPORATE_VERIFICATION_ERROR_CODES } from '@/apps/bh_benefits/upgrade/helpers/corporateVerificationErrors';
import { currentJwt, forceRefreshJwt } from '@/lib/jwt';

type PageState = 'loading' | 'success' | 'logged_out' | 'wrong_account' | 'existing_premium' | 'error';

export default {
  name: 'VerifyCorporateUpgrade',
  components: {
    Card,
    NavigationLogo,
    Spinner,
    VerifyCorporateUpgradeSuccess,
    VerifyCorporateUpgradeLoggedOut,
    VerifyCorporateUpgradeWrongAccount,
    VerifyCorporateUpgradeExistingPremium,
    VerifyCorporateUpgradeInvalidLink,
  },
  data(): { loading: boolean; pageState: PageState; maskedMemberEmail: string } {
    return {
      loading: true,
      pageState: 'loading',
      maskedMemberEmail: '',
    };
  },
  computed: {
    token(): string {
      return this.$route.params.token;
    },
    loginUrl(): string {
      return `/login?return_path=${encodeURIComponent(this.$route.fullPath)}`;
    },
    isAuthenticated(): boolean {
      return !!currentJwt();
    },
  },
  mounted() {
    this.initializePage();
  },
  methods: {
    async initializePage(): Promise<void> {
      this.loading = true;
      this.pageState = 'loading';

      try {
        const tokenPreview = await this.fetchTokenPreview();

        if (!tokenPreview) {
          this.pageState = 'error';
          return;
        }

        this.maskedMemberEmail = tokenPreview.maskedMemberEmail;

        if (!this.isAuthenticated) {
          this.pageState = 'logged_out';
          return;
        }

        await this.consumeToken();
      } catch {
        this.pageState = 'error';
      } finally {
        this.loading = false;
      }
    },
    async fetchTokenPreview(): Promise<{ maskedMemberEmail: string } | null> {
      const result = await this.$apollo.query({
        query: CORPORATE_VERIFICATION_TOKEN_QUERY,
        variables: {
          token: this.token,
        },
        fetchPolicy: 'network-only',
      });

      return result.data?.corporateVerificationToken ?? null;
    },
    async consumeToken(): Promise<void> {
      this.loading = true;
      this.pageState = 'loading';

      try {
        const result = await this.$apollo.mutate({
          mutation: CONSUME_CORPORATE_VERIFICATION_TOKEN,
          variables: {
            token: this.token,
          },
        });

        const consumeResult = result.data?.corp?.consumeCorporateVerificationToken;

        await this.refreshSessionAfterCorporateUpgrade();
        this.pageState = consumeResult?.hadExistingPremium ? 'existing_premium' : 'success';
      } catch (error: any) {
        this.pageState = this.pageStateFromError(error);
      } finally {
        this.loading = false;
      }
    },
    async refreshSessionAfterCorporateUpgrade(): Promise<void> {
      await forceRefreshJwt();
      await this.$store.dispatch('account/fetchAccountData');
    },
    pageStateFromError(error: any): PageState {
      const errorCode = error?.graphQLErrors?.[0]?.extensions?.error_code;

      if (errorCode === CORPORATE_VERIFICATION_ERROR_CODES.WRONG_ACCOUNT) {
        return 'wrong_account';
      }

      return 'error';
    },
  },
};
</script>
